import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthGuard } from '../../../../hooks/useAuthGuard';
import {
  Layout,
  Typography,
  Select,
  Button,
  Card,
  List,
  Flex,
  theme,
  Grid,
  ColorPicker,
  message,
  Input,
  Upload,
  Tooltip,
} from 'antd';
import { HomeOutlined, SendOutlined, RightOutlined, PlayCircleOutlined, LeftOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColorPickerProps } from 'antd/es/color-picker';
import { useAssistant } from '../../../../contexts/AssistantContext';
import { useAccount } from '../../../../store';
import { AppearanceState } from '../../types';
import { getAppearanceStateFromAssistant, toColorString } from '../../utils';
import { assistantService } from '../../../assistant-management/services/assistantManagementService';
import { INBOX_MESSAGES } from '../../constants';
import { Chatbot } from '../../../../Components/Chatbot/Chatbot';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('features-assistants');

const { Content } = Layout;
const { Title, Text, Link } = Typography;
const { useToken } = theme;
const { useBreakpoint } = Grid;

export function Appearance(): React.ReactElement {
  useAuthGuard(); // Proteger esta página con autenticación
  
  const navigate = useNavigate();
  const { currentAccount } = useAccount();
  const { token } = useToken();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { currentAssistant, setAssistant } = useAssistant();
  const [isPreviewOpen, setIsPreviewOpen] = useState(true);

  const [appearanceState, setAppearanceState] = useState<AppearanceState>({
    primaryColor: (currentAssistant as any)?.webchatConfig?.colorTitle || '#E3F571',
    headerColor: (currentAssistant as any)?.webchatConfig?.colorMain || '#7E70B3',
    greetingMessage: (currentAssistant as any)?.webchatConfig?.greeting || 'Hello! How can I help you today?',
    iconColor: (currentAssistant as any)?.webchatConfig?.iconBgColor || '#000000',
    chatIconSvg: (currentAssistant as any)?.webchatConfig?.chatIconSvg || undefined,
  });

  // Update appearance state when currentAssistant changes
  useEffect(() => {
    if ((currentAssistant as any)?.webchatConfig) {
      logger.info('Reading assistant data:', {
        assistantName: currentAssistant?.name,
        assistantId: currentAssistant?.id,
        fullAssistant: currentAssistant,
        colorTitle: (currentAssistant as any).webchatConfig.colorTitle,
        colorMain: (currentAssistant as any).webchatConfig.colorMain,
        greeting: (currentAssistant as any).webchatConfig.greeting,
        iconBgColor: (currentAssistant as any).webchatConfig.iconBgColor
      });
      
      setAppearanceState({
        primaryColor: (currentAssistant as any).webchatConfig.colorTitle,
        headerColor: (currentAssistant as any).webchatConfig.colorMain,
        greetingMessage: (currentAssistant as any).webchatConfig.greeting,
        iconColor: (currentAssistant as any).webchatConfig.iconBgColor || '#000000',
        chatIconSvg: (currentAssistant as any).webchatConfig.chatIconSvg || undefined,
      });
    }
  }, [currentAssistant]);

  // Default cloud WebSocket URL
  const DEFAULT_WEBSOCKET_URL = process.env.REACT_APP_WS_WEB_CHATBOT;

  const handleSave = async () => {
    if (!currentAssistant) {
      message.error('No assistant selected');
      return;
    }

    try {
      const appearanceData = {
        appearanceState: {
          primaryColor: toColorString(appearanceState.primaryColor),
          headerColor: toColorString(appearanceState.headerColor),
          backgroundColor: toColorString(appearanceState.primaryColor), // Default to primary if not set
          textColor: '#FFFFFF', // Default text color
          buttonColor: toColorString(appearanceState.primaryColor), // Default to primary if not set
          backgroundMessageColor: toColorString(appearanceState.headerColor), // Use header color for messages
          iconColor: toColorString(appearanceState.iconColor),
          greetingMessage: appearanceState.greetingMessage,
          chatIconSvg: appearanceState.chatIconSvg,
        },
        currentAssistant: {
          id: currentAssistant.id,
          name: currentAssistant.name,
          businessName: (currentAssistant as any).businessName || '',
          communicationStyle: (currentAssistant as any).communicationStyle || '',
          domainTopic: (currentAssistant as any).domainTopic || '',
          specialist: (currentAssistant as any).specialist || '',
          fallbackResponse: (currentAssistant as any).fallbackResponse || '',
          webchatConfig: (currentAssistant as any).webchatConfig || {}
        },
      };

      logger.info('Saving appearance data:', appearanceData);
      const response = await assistantService.handleSave(appearanceData);
      logger.info('Save response:', response);

      // Handle different response structures
      const updatedAssistant = response?.data?.updatedChatbotData || response?.data;

      if (updatedAssistant && (updatedAssistant.webchatConfig || updatedAssistant.id)) {
        logger.info('Updated assistant data:', updatedAssistant);

        // Update local state if webchatConfig exists
        if (updatedAssistant.webchatConfig) {
          setAppearanceState({
            primaryColor: updatedAssistant.webchatConfig.colorTitle,
            headerColor: updatedAssistant.webchatConfig.colorMain,
            greetingMessage: updatedAssistant.webchatConfig.greeting,
            iconColor: updatedAssistant.webchatConfig.iconBgColor || '#000000',
            chatIconSvg: updatedAssistant.webchatConfig.chatIconSvg || undefined,
          });
        }

        // Refresh the assistant context to get the latest data
        await setAssistant(currentAssistant.id);

        message.success('Appearance settings saved successfully');
      } else {
        // If we can't find the expected data structure, still refresh and show success
        // since the server responded successfully
        logger.info('Unexpected response structure, but request was successful');
        await setAssistant(currentAssistant.id);
        message.success('Appearance settings saved successfully');
      }
    } catch (error) {
      logger.error('Error saving appearance:', error);
      message.error('Error saving appearance settings');
    }
  };

  // Define color settings with labels and state
  const colorSettings = [
    {
      label: 'Primary Color',
      value: appearanceState.primaryColor,
      onChange: (value: ColorPickerProps['value']) => {
        setAppearanceState(prev => ({ ...prev, primaryColor: value }));
        if ((currentAssistant as any)?.webchatConfig) {
          (currentAssistant as any).webchatConfig.colorMain = toColorString(value);
        }
      },
    },
    {
      label: 'Secondary Color',
      value: appearanceState.headerColor,
      onChange: (value: ColorPickerProps['value']) => {
        setAppearanceState(prev => ({ ...prev, headerColor: value }));
        if ((currentAssistant as any)?.webchatConfig) {
          (currentAssistant as any).webchatConfig.colorTitle = toColorString(value);
        }
      },
    },
    {
      label: 'Icon Color',
      value: appearanceState.iconColor,
      onChange: (value: ColorPickerProps['value']) => {
        setAppearanceState(prev => ({ ...prev, iconColor: value }));
      },
    },
  ];

  return (
    <Card>
    <Layout
      style={{
        background: 'transparent',
        height: '100%',
      }}
    >
      <Content
        style={{
          padding: isMobile ? token.paddingSM : 0,
          position: 'relative',
          height: 'fit-content',
          minHeight: '700px'
        }}
      >
        <Flex
          justify="space-between"
          align="flex-start"
          style={{ marginBottom: token.margin }}
          vertical={isMobile}
          gap={isMobile ? token.marginSM : 0}
        >
          <Title level={isMobile ? 2 : 4} style={{ margin: 0 }}>
            Appearance
          </Title>
          <Button 
            type="primary" 
            style={{ width: isMobile ? '100%' : 'auto' }} 
            onClick={handleSave} 
            icon={<PlayCircleOutlined />}
            id="appearance-save-publish-button"
          >
            Save and publish
          </Button>
        </Flex>

        <Flex gap={token.marginMD} style={{ width: '100%', padding:'0px', height: '100%' }} vertical={isMobile}>
          {/* Opciones de configuración */}
          <Flex
            vertical
            gap={token.marginSM}
            style={{
              width: '100%',
            }}
          >
            {colorSettings.map((setting, index) => (
              <Flex key={index} align="center" gap={token.marginSM} style={{ width: '100%' }}>
                <Text style={{ width: 200, flexShrink: 0 }}>{setting.label}:</Text>
                <ColorPicker
                  value={setting.value}
                  onChange={(color) => setting.onChange(color)}
                  showText
                  style={{ flexGrow: 0 }}
                />
              </Flex>
            ))}
            
            {/* Chat Icon SVG Upload */}
            <Flex align="flex-start" gap={token.marginSM} style={{ width: '100%' }}>
              <Text style={{ width: 200, flexShrink: 0, paddingTop: 6 }}>Chat Icon (SVG):</Text>
              <Flex vertical gap={token.marginXS}>
                <Flex align="center" gap={token.marginSM}>
                  <Upload
                    accept=".svg"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      if (!file.name.endsWith('.svg') && file.type !== 'image/svg+xml') {
                        message.error('Only SVG files are allowed');
                        return Upload.LIST_IGNORE;
                      }
                      // 50 KB cap to keep DynamoDB item well under the 400 KB limit
                      if (file.size > 50 * 1024) {
                        message.error('SVG file must be smaller than 50 KB');
                        return Upload.LIST_IGNORE;
                      }
                      const reader = new FileReader();
                      reader.onload = (e) => {
                        const rawSvg = e.target?.result as string;
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(rawSvg, 'image/svg+xml');

                        // Reject malformed SVG (DOMParser signals errors via a <parsererror> element)
                        if (doc.querySelector('parsererror')) {
                          message.error('Invalid SVG file');
                          return;
                        }

                        // Strip dangerous elements: scripts, foreign content, use (href spoofing)
                        doc.querySelectorAll('script, foreignObject, use').forEach(el => el.remove());

                        doc.querySelectorAll('*').forEach(el => {
                          // Remove all event-handler attributes (onclick, onload, onbegin, etc.)
                          Array.from(el.attributes).forEach(attr => {
                            if (attr.name.toLowerCase().startsWith('on')) el.removeAttribute(attr.name);
                          });
                          // Remove javascript: and data: URIs from href / xlink:href
                          ['href', 'xlink:href', 'action', 'src'].forEach(attrName => {
                            const val = el.getAttribute(attrName) || '';
                            if (/^\s*(javascript|data):/i.test(val)) el.removeAttribute(attrName);
                          });
                          // Normalize fill/stroke to currentColor so the icon inherits Icon Color
                          const fill = el.getAttribute('fill');
                          if (fill && fill !== 'none') el.setAttribute('fill', 'currentColor');
                          const stroke = el.getAttribute('stroke');
                          if (stroke && stroke !== 'none') el.setAttribute('stroke', 'currentColor');
                        });

                        const svgContent = new XMLSerializer().serializeToString(doc.documentElement);
                        setAppearanceState(prev => ({ ...prev, chatIconSvg: svgContent }));
                      };
                      reader.readAsText(file);
                      return false;
                    }}
                  >
                    <Button icon={<UploadOutlined />}>Upload SVG</Button>
                  </Upload>
                  {appearanceState.chatIconSvg && (
                    <Tooltip title="Remove icon">
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => setAppearanceState(prev => ({ ...prev, chatIconSvg: undefined }))}
                      />
                    </Tooltip>
                  )}
                </Flex>
                {appearanceState.chatIconSvg && (
                  <div
                    style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    dangerouslySetInnerHTML={{ __html: appearanceState.chatIconSvg }}
                  />
                )}
              </Flex>
            </Flex>

            {/* Greeting Message Input */}
            <Flex align="center" gap={token.marginSM} style={{ width: '50%' }}>
              <Text style={{ flexShrink: 0 }}>Greeting Message:</Text>
              <Input.TextArea
                value={appearanceState.greetingMessage}
                onChange={(e) => {
                  setAppearanceState(prev => ({ ...prev, greetingMessage: e.target.value }));
                  if ((currentAssistant as any)?.webchatConfig) {
                    (currentAssistant as any).webchatConfig.greeting = e.target.value;
                  }
                }}
                placeholder="Enter your greeting message"
                rows={7}
                style={{ flexGrow: 1 }}
              />
            </Flex>
          </Flex>
        </Flex>

        {/* Chatbot embebido posicionado absolutamente en el Content */}
        <div
          style={{
            position: isMobile ? 'relative' : 'absolute',
            bottom: isMobile ? 'auto' : '20px',
            right: isMobile ? 'auto' : '20px',
            zIndex: 1000,
            marginTop: isMobile ? token.marginMD : 0,
            width: isMobile ? '100%' : 'auto',
            display: 'flex',
            justifyContent: isMobile ? 'center' : 'flex-end',
          }}
        >
          <Chatbot
            wsUrl={currentAssistant?.websocketUrl || DEFAULT_WEBSOCKET_URL}
            chatbotId={currentAssistant?.id || '0'}
            channelId="preview"
            urlApi={`${process.env.REACT_APP_API_HOST}/chatbot`}
            isWeb={false}
            isPreview={true}
            primaryColor={toColorString(appearanceState.primaryColor)}
            headerColor={toColorString(appearanceState.headerColor)}
            iconColor={toColorString(appearanceState.iconColor)}
            chatIconSvg={appearanceState.chatIconSvg}
            botName={currentAssistant?.name}
          />
        </div>
      </Content>
    </Layout>
    </Card>
  );
}
